import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material';
import {
  DashboardService,
  RecordIssue,
  RecordTypeData,
  TimeSeriesData,
  FinanceData,
  FundraisingProject,
  RecentActivityItem,
  EmailCoverage
} from '../dashboard.service';
import { OldboysService } from '../oldboys.service';
import { OldboyDialogComponent } from '../oldboy-dialog/oldboy-dialog.component';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css']
})
export class HomeComponent implements OnInit {

  recordIssues: RecordIssue[] = [];
  recordTypes: RecordTypeData[] = [];
  paidMembersData: TimeSeriesData[] = [];
  financeData: FinanceData[] = [];
  fundraisingProjects: FundraisingProject[] = [];
  recentActivity: RecentActivityItem[] = [];
  emailCoverage: EmailCoverage = { withEmail: 0, total: 0, percentage: 0 };

  totalRecords = 0;
  outstandingFees = 0;

  showAllIssues = false;
  displayedIssues: RecordIssue[] = [];

  loading = false;

  // Dynamic SVG paths
  financeChartPath = '';
  financeChartPoints: { cx: number; cy: number }[] = [];
  financeYLabels: string[] = [];

  paidMembersChartPath = '';
  paidMembersYLabels: string[] = [];
  paidMembersStartLabel = '';
  paidMembersEndLabel = '';
  paidMembersCurrentLabel = '';

  donutGradient = '';

  constructor(
    private dashboardService: DashboardService,
    private oldboysService: OldboysService,
    private dialog: MatDialog
  ) { }

  ngOnInit() {
    this.loadDashboardData();
  }

  private pendingRequests = 0;

  loadDashboardData() {
    this.loading = true;
    this.pendingRequests = 7;

    const done = () => {
      this.pendingRequests--;
      if (this.pendingRequests <= 0) {
        this.loading = false;
      }
    };

    this.dashboardService.getRecordsWithIssues().subscribe(issues => {
      this.recordIssues = issues;
      this.displayedIssues = this.showAllIssues ? issues : issues.slice(0, 3);
      done();
    }, done);

    this.dashboardService.getRecordTypeDistribution().subscribe(result => {
      this.recordTypes = result.distribution;
      this.totalRecords = result.totalRecords;
      this.buildDonutGradient();
      done();
    }, done);

    this.dashboardService.getPaidUpMembersOverTime().subscribe(data => {
      this.paidMembersData = data;
      this.buildPaidMembersChart();
      done();
    }, done);

    this.dashboardService.getFinanceSummary().subscribe(result => {
      this.financeData = result.months;
      this.outstandingFees = result.outstandingFees;
      this.buildFinanceChart();
      done();
    }, done);

    this.dashboardService.getEmailCoverage().subscribe(result => {
      this.emailCoverage = result;
      done();
    }, done);

    this.dashboardService.getFundraisingProjects().subscribe(projects => {
      this.fundraisingProjects = projects;
      done();
    }, done);

    this.dashboardService.getRecentActivity().subscribe(activity => {
      this.recentActivity = activity;
      done();
    }, done);
  }

  buildDonutGradient() {
    if (!this.recordTypes.length || !this.totalRecords) {
      this.donutGradient = '#e0e0e0';
      return;
    }
    let currentDeg = 0;
    const stops: string[] = [];
    this.recordTypes.forEach(type => {
      const deg = (type.count / this.totalRecords) * 360;
      stops.push(`${type.color} ${currentDeg}deg ${currentDeg + deg}deg`);
      currentDeg += deg;
    });
    this.donutGradient = 'conic-gradient(' + stops.join(', ') + ')';
  }

  buildFinanceChart() {
    if (!this.financeData.length) return;

    const amounts = this.financeData.map(d => d.amount);
    const max = Math.max(...amounts);
    const min = Math.min(...amounts);
    const range = max - min || 1;

    // Chart area: x from 40 to 250, y from 20 to 100
    const xStart = 40, xEnd = 250, yTop = 20, yBottom = 100;
    const xStep = this.financeData.length > 1 ? (xEnd - xStart) / (this.financeData.length - 1) : 0;

    const points = this.financeData.map((d, i) => ({
      cx: xStart + i * xStep,
      cy: yBottom - ((d.amount - min) / range) * (yBottom - yTop)
    }));

    this.financeChartPoints = points;
    this.financeChartPath = 'M ' + points.map(p => `${p.cx} ${p.cy}`).join(' L ');

    // Y-axis labels
    const step = range / 3;
    this.financeYLabels = [
      '$' + Math.round(max / 1000) + 'k',
      '$' + Math.round((max - step) / 1000) + 'k',
      '$' + Math.round((max - 2 * step) / 1000) + 'k',
      '$' + Math.round(min / 1000) + 'k'
    ];
  }

  buildPaidMembersChart() {
    if (!this.paidMembersData.length) return;

    const values = this.paidMembersData.map(d => d.value);
    const max = Math.max(...values);
    const min = Math.min(...values);
    const range = max - min || 1;

    // Chart area: x from 50 to 510, y from 20 to 120
    const xStart = 50, xEnd = 510, yTop = 20, yBottom = 120;
    const xStep = this.paidMembersData.length > 1 ? (xEnd - xStart) / (this.paidMembersData.length - 1) : 0;

    const points = this.paidMembersData.map((d, i) => ({
      x: xStart + i * xStep,
      y: yBottom - ((d.value - min) / range) * (yBottom - yTop)
    }));

    this.paidMembersChartPath = 'M ' + points.map(p => `${p.x} ${p.y}`).join(' L ');

    // Y-axis labels
    const step = range / 4;
    this.paidMembersYLabels = [];
    for (let i = 0; i <= 4; i++) {
      this.paidMembersYLabels.push(this.formatNumber(Math.round(max - i * step)));
    }

    // Date range labels
    const firstDate = new Date(this.paidMembersData[0].date);
    const lastDate = new Date(this.paidMembersData[this.paidMembersData.length - 1].date);
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    this.paidMembersStartLabel = monthNames[firstDate.getMonth()] + ' ' + firstDate.getFullYear();
    this.paidMembersEndLabel = monthNames[lastDate.getMonth()] + ' ' + lastDate.getFullYear();
    const lastValue = values[values.length - 1];
    this.paidMembersCurrentLabel = 'Current: ~' + this.formatNumber(lastValue);
  }

  toggleShowAllIssues() {
    this.showAllIssues = !this.showAllIssues;
    this.displayedIssues = this.showAllIssues ? this.recordIssues : this.recordIssues.slice(0, 3);
  }

  getIssueIcon(type: string): string {
    switch (type) {
      case 'missing_year': return 'event_busy';
      case 'email_bounce': return 'mail_outline';
      case 'incomplete_data': return 'warning';
      default: return 'info';
    }
  }

  getIssueColor(type: string): string {
    switch (type) {
      case 'missing_year': return 'warn';
      case 'email_bounce': return 'accent';
      case 'incomplete_data': return 'primary';
      default: return 'primary';
    }
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-NZ', {
      style: 'currency',
      currency: 'NZD'
    }).format(amount);
  }

  formatNumber(num: number): string {
    return new Intl.NumberFormat().format(num);
  }

  getTimeAgo(timestamp: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - new Date(timestamp).getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffMinutes < 60) {
      return `${diffMinutes} minutes ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hours ago`;
    } else {
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays} days ago`;
    }
  }

  onRecordClick(issue: RecordIssue) {
    this.oldboysService.getOldBoy(issue.id).subscribe(oldboy => {
      if (oldboy) {
        this.dialog.open(OldboyDialogComponent, {
          width: '90vw',
          maxWidth: '1200px',
          data: { oldboy }
        });
      }
    });
  }

  onActivityClick(activity: RecentActivityItem) {
    if (activity.type === 'update' || activity.type === 'payment') {
      this.oldboysService.getOldBoy(activity.id).subscribe(oldboy => {
        if (oldboy) {
          this.dialog.open(OldboyDialogComponent, {
            width: '90vw',
            maxWidth: '1200px',
            data: { oldboy }
          });
        }
      });
    }
  }

  refreshDashboard() {
    this.loadDashboardData();
  }
}
